<template>
  <div class="grid-header-container" :style="headerHeight">
    <div class="fiduciary-and-fiduciary-client">
      <selected-fiduciary></selected-fiduciary>
      <div class="vertical-line"></div>
      <client-selector
        :selectedText="selectedClientName"
        :canSelect="canClientSelect"
        @onSelect="toggleClientList"
      />
    </div>
    <transition name="scale">
      <clientList
        v-show="clientListVisible"
        :fiduciary="selectedFiduciary"
        :selectedClient="selectedClient"
        @closeClientList="toggleClientList"
        @updateClientFunds="updateClient"
      />
    </transition>
    <navigation-list
      v-if="selectedClient !== 0"
      :items="allowedTabs"
      :selected="selectedTab"
      @selectedItem="selectTab"
    />
    <div class="search-field-container">
      <mds-search-field
        class="search-field"
        placeholder="Search by fund name, ticker, SecID"
        label="Search"
        on-dark
        v-model="searchText"
        :disabled="isSearchDisabled"
      >
      </mds-search-field>
    </div>
  </div>
</template>
<script>
import { get } from 'lodash';
import { EventBus } from '@/utils/event-bus';
import { mapActions, mapState } from 'vuex';
import NavigationList from '@/components/atoms/NavigationList';
import ClientList from '@/components/atoms/ClientList';
import ClientSelector from '@/components/atoms/ClientSelector';
import SelectedFiduciary from '@/components/atoms/SelectedFiduciary';
import MdsSearchField from '@mds/search-field';
import { FIDUCIARIES, USER_ROLES } from '@/utils/app-constants';

export default {
  name: 'grid-header',
  components: {
    ClientSelector,
    ClientList,
    MdsSearchField,
    NavigationList,
    SelectedFiduciary,
  },
  data() {
    return {
      tabs: {
        items: [
          {
            id: 'universe',
            text: 'Universe',
            enabled: () => true,
          },
          {
            id: 'lineups',
            text: 'Lineups',
            enabled: () => true,
          },
          {
            id: 'lineup-changes',
            text: '3(38) Lineup Changes',
            enabled: () => true,
          },
          {
            id: 'replacements-new',
            text: '3(21) Replacements',
            enabled: () => this.selectedClientHasReplacements,
          },
          {
            id: 'fund-review',
            text: 'Fund Review Panel',
            enabled: this.isFundReviewEnabled,
          },
        ],
        selected: 'universe',
      },
      searchText: '',
    };
  },
  watch: {
    searchText(text) {
      this.$store.dispatch('setSearchTerm', text);
    },
  },
  computed: {
    ...mapState({
      policies: (state) => state.user.policies,
      selectedFiduciary: (state) => state.user.fiduciary.id,
      selectedClient: (state) => state.grid.client.selected,
      selectedClientName: (state) => state.grid.client.name,
      selectedClientHasReplacements: (state) => state.grid.client.hasReplacements,
      clientListVisible: (state) => state.grid.client.listVisible,
      selectedTab: (state) => state.grid.tab,
      searchTerm: (state) => state.grid.searchTerm,
      selectedFiduciaryCode: (state) => state.user.fiduciary.code,
      userRole: (state) => state.user.userRole,
    }),
    currentViewName() {
      return this.selectedClient > 0 ? 'one-client' : 'all-clients';
    },
    localPolicyOptions() {
      // Tabs are applied to 1 Client View
      if (this.currentViewName === 'all-clients') {
        return get(this.policies, `grid.views.${this.currentViewName}`, {});
      }
      return get(this.policies, `grid.views.${this.currentViewName}.tabs.${this.selectedTab}`, {});
    },
    headerHeight() {
      // IE does not recognize the dynamic flex change
      return this.selectedClient ? 'flex: 0 0 111px;' : 'flex: 0 0 60px';
    },
    allowedTabs() {
      // Filter out allowed tabs
      return this.tabs.items.filter((tab) => tab.enabled());
    },
    canClientSelect() {
      return get(this.localPolicyOptions, 'options["select-client"]', false);
    },
    isSearchDisabled() {
      return !get(this.localPolicyOptions, 'options.search', false);
    },
  },
  created() {
    EventBus.$on('search-clear', () => {
      this.searchText = '';
    });
    this.searchText = this.searchTerm;
  },
  methods: {
    ...mapActions([
      'setSavedFilter',
      'setSortConfig',
      'setProduct',
      'setSearchTerm',
    ]),
    toggleClientList() {
      if (this.localPolicyOptions.options['select-client']) {
        this.$store.dispatch('setClientListVisible', !this.clientListVisible);
      }
    },
    isFundReviewEnabled() {
      // Show Fund Review Panel only if Fiduciary is Morningstar and User is Consultant.
      return this.selectedFiduciaryCode === FIDUCIARIES.MONRNINGSTAR && this.userRole === USER_ROLES.CONSULTANT;
    },
    updateClient(client) {
      // if else loop to decide if the client value is 0(zero or not)
      if (client) {
        // this will route us to one client vue
        this.$router.push({
          name: 'clientGrid',
          params: { clientId: `${client}` },
        }).catch(() => {}); // Avoided redundant navigation error on console
      } else {
        // this will route us to all client vue
        this.$router.push({
          name: 'grid',
        }).catch(() => {}); // Avoided redundant navigation error on console
      }
    },
    selectTab(selected) {
      // Clear product, saved filter and sort config
      this.searchText = '';
      this.$store.dispatch('setSearchTerm', '');
      this.setSavedFilter(null);
      this.setSortConfig(null);
      this.setProduct(0, 'All Products', 'Universe');

      this.tabs.selected = selected;
      this.$store.dispatch('setTab', selected);
    },
  },
};
</script>
<style lang="scss">
.grid-header-container {
  flex: 0 0 60px;
  position: relative;
  background-color: #12698d;
  color: $mds-color-white;
}

.scale-enter-active,
.scale-leave-active {
  transition: all 0.1s ease-out;
  transform-origin: top left;
}
.scale-enter,
.scale-leave-to {
  transform: scaleY(0);
  opacity: 0;
}

.fiduciary-and-fiduciary-client {
  display: flex;
  flex-direction: row;
  line-height: 0;
}

.vertical-line {
  border-left: $mds-space-quarter-x solid $mds-background-color-white;
  margin: $mds-space-1-x 0 $mds-space-1-x 0;
}

.search-field-container {
  position: absolute;
  right: $mds-space-2-x;
  top: $mds-space-2-and-a-half-x;
  width: 280px;
}
</style>

<style module lang="scss">
 // MDS overrides only
  @import '@mds/constants';

  @mixin placeholder {
    &::-webkit-input-placeholder {
      @content;
    }
    &:-moz-input-placeholder {
      @content;
    }
    &::-moz-input-placeholder {
      @content;
    }
    &:-moz-placeholder {
      @content;
    }
    &::-moz-placeholder {
      @content;
    }
    &:-ms-input-placeholder {
      @content;
    }
  }

  :global(.search-field-container) {
    .mds-component--on-dark {
      .mds-search-field {
        &__input {
          border: 1px solid $mds-color-white;
          @include placeholder {
            color: $mds-color-white;
          }
        }
        &__search-icon {
          fill: $mds-color-white;
          stroke: $mds-color-white;
        }
        &__clear-button {
          .mds-icon {
            fill: $mds-color-white;
            stroke: $mds-color-white;
          }
        }
      }
    }
  }
</style>
